import throttle from 'lodash/throttle';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Handle } from './Handle';
import { Range } from './Range';
import { Thumbnails } from './Thumbnails';
import { BAR_HEIGHT, HANDLE_WIDTH } from './types';
import { useTrimBarDrag } from './useTrimBarDrag';
import { calculateConstrainedPositions, calculateValue } from './utils';

export default function TrimBar({
  value,
  trimStart,
  trimEnd,
  maxValue,
  minGap = 0,
  maxGap,
  thumbnails,
  color = 'gray',
  bgColor,
  format,
  hideHandleInfo,
  onValueChange,
  onTrimChange,
}: {
  value: number;
  trimStart: number;
  trimEnd: number;
  maxValue: number;
  minGap?: number;
  maxGap?: number;
  thumbnails?: string[];
  color?: 'gray' | 'violet';
  bgColor?: string;
  format?: (value: number) => string;
  hideHandleInfo?: boolean;
  onValueChange: (value: number) => void;
  onTrimChange: (start: number, end: number) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [startPos, setStartPos] = useState((100 * trimStart) / maxValue);
  const [endPos, setEndPos] = useState((100 * trimEnd) / maxValue);

  const minGapPercent = (minGap / maxValue) * 100;
  const maxGapPercent = maxGap ? (maxGap / maxValue) * 100 : 100;

  const throttledTrimChange = useCallback(throttle(onTrimChange, 16), [
    onTrimChange,
  ]);

  const { isDragging, handleDragStart, handleDragMove, handleDragEnd } =
    useTrimBarDrag({
      startPos,
      endPos,
      setStartPos,
      setEndPos,
      trackRef,
      minGapPercent,
      maxGapPercent,
    });

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  const startValue = calculateValue.fromPosition(startPos, maxValue);
  const endValue = calculateValue.fromPosition(endPos, maxValue);

  useEffect(() => {
    // Only call throttledTrimChange when user is actively dragging
    if (isDragging) {
      throttledTrimChange(startValue, endValue);
    }
    return () => throttledTrimChange.cancel();
  }, [startValue, endValue, isDragging, throttledTrimChange]);

  useEffect(() => {
    const [validatedStart, validatedEnd] = calculateConstrainedPositions(
      trimStart,
      trimEnd,
      maxValue,
      minGapPercent,
      maxGapPercent,
    );
    setStartPos(validatedStart);
    setEndPos(validatedEnd);
  }, [trimStart, trimEnd, maxValue, minGapPercent, maxGapPercent]);

  // Show first and last frame when handles are dragged
  useEffect(() => {
    if (isDragging === 'end') {
      onValueChange(endValue - trimStart);
    } else if (isDragging === 'start' || isDragging === 'range') {
      onValueChange(startValue - trimStart);
    }
  }, [isDragging, startValue, endValue, trimStart, onValueChange]);

  return (
    <div
      className="bg-gray-15 dark:bg-gray-60 relative z-0 w-full select-none rounded-lg"
      style={{
        height: BAR_HEIGHT,
        background: bgColor
          ? `linear-gradient(180deg, ${bgColor} -30%, #e3e7e9 100%) no-repeat bottom`
          : undefined,
      }}
    >
      <Thumbnails thumbnails={thumbnails} />

      <div
        ref={trackRef}
        className="relative h-full"
        style={{
          width: `calc(100% - ${HANDLE_WIDTH * 2}px)`,
          marginLeft: HANDLE_WIDTH,
        }}
      >
        <Handle
          open={isDragging === 'start' || isDragging === 'range'}
          side="start"
          pos={startPos}
          value={startValue}
          handleMouseDown={handleDragStart}
          color={color}
          format={format}
          hideHandleInfo={hideHandleInfo}
        />
        <Range
          value={value}
          onChange={(v) => {
            if (!isDragging) {
              onValueChange(v);
            }
          }}
          handleMouseDown={handleDragStart}
          minValue={0}
          maxValue={trimEnd - trimStart}
          color={color}
          format={format}
          style={{
            left: `${startPos}%`,
            width: `${endPos - startPos}%`,
          }}
          hideThumb={!!isDragging}
        />
        <Handle
          open={isDragging === 'end' || isDragging === 'range'}
          side="end"
          pos={endPos}
          value={endValue}
          handleMouseDown={handleDragStart}
          color={color}
          format={format}
          hideHandleInfo={hideHandleInfo}
        />
      </div>
    </div>
  );
}
